import * as THREE from "three";

/** Quader aus Grundriss-Koordinaten (x, y) und Höhen (z). */
export function boxPlan(xa: number, xb: number, ya: number, yb: number, za: number, zb: number,
  m: THREE.Material | THREE.Material[], shadow = true): THREE.Mesh {
  const g = new THREE.BoxGeometry(Math.max(xb - xa, 0.001), Math.max(zb - za, 0.001), Math.max(yb - ya, 0.001));
  const o = new THREE.Mesh(g, m);
  o.position.set((xa + xb) / 2, (za + zb) / 2, -(ya + yb) / 2);
  o.castShadow = shadow; o.receiveShadow = true;
  return o;
}

export type Pt = [number, number];

/** Polygon im Grundriss, senkrecht extrudiert. */
export function prismPlan(pts: Pt[], holes: Pt[][] | null, za: number, zb: number, m: THREE.Material): THREE.Mesh {
  const s = new THREE.Shape(pts.map((p) => new THREE.Vector2(p[0], p[1])));
  for (const h of holes || []) s.holes.push(new THREE.Path(h.map((q) => new THREE.Vector2(q[0], q[1]))));
  const g = new THREE.ExtrudeGeometry(s, { depth: zb - za, bevelEnabled: false });
  const o = new THREE.Mesh(g, m);
  o.rotation.x = -Math.PI / 2; o.position.y = za;
  o.castShadow = true; o.receiveShadow = true;
  return o;
}

export const rect = (x0: number, y0: number, x1: number, y1: number): Pt[] => [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];

/** Öffnung in einer Wand: a0/a1 entlang der Wand (absolute Planmaße), b0/b1 Höhe über Fußboden */
export interface Opening { a0: number; a1: number; b0: number; b1: number; k: OpeningKind }
export type OpeningKind = "win" | "door" | "gdoor" | "edoor" | "block" | "gate" | "hole";

export function wallMesh(xa: number, ya: number, xb: number, yb: number, alongX: boolean, start: number, L: number,
  topFn: (u: number) => number, ridgeU: number | null, ops: Opening[], m: THREE.Material, zBase: number): THREE.Mesh {
  const t = alongX ? yb - ya : xb - xa;
  const notches = ops.filter((o) => o.b0 <= 0.001).map((o) => ({ a0: o.a0 - start, a1: o.a1 - start, b1: o.b1 })).sort((a, b) => a.a0 - b.a0);
  const raw: Pt[] = [[0, 0]];
  for (const n of notches) raw.push([n.a0, 0], [n.a0, n.b1], [n.a1, n.b1], [n.a1, 0]);
  raw.push([L, 0], [L, topFn(L)]);
  if (ridgeU != null && ridgeU > 0.01 && ridgeU < L - 0.01) raw.push([ridgeU, topFn(ridgeU)]);
  raw.push([0, topFn(0)]);
  const pts: Pt[] = [];
  for (const p of raw) { const q = pts[pts.length - 1]; if (!q || Math.hypot(q[0] - p[0], q[1] - p[1]) > 1e-4) pts.push(p); }
  const s = new THREE.Shape(pts.map((p) => new THREE.Vector2(p[0], p[1])));
  for (const o of ops.filter((o) => o.b0 > 0.001)) {
    const a0 = o.a0 - start, a1 = o.a1 - start, p = new THREE.Path();
    p.moveTo(a0, o.b0); p.lineTo(a1, o.b0); p.lineTo(a1, o.b1); p.lineTo(a0, o.b1); p.closePath(); s.holes.push(p);
  }
  const g = new THREE.ExtrudeGeometry(s, { depth: t, bevelEnabled: false });
  const o = new THREE.Mesh(g, m); o.castShadow = o.receiveShadow = true;
  if (alongX) o.position.set(xa, zBase, -yb);
  else { o.rotation.y = Math.PI / 2; o.position.set(xa, zBase, -ya); }
  return o;
}
